import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { ActiveCall } from '@/redux/app/reducers/calls';

const Panel = styled.div`
  width: 320px;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #d9d9d9);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 16px;
  pointer-events: auto;
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Detail = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary, #666);
  margin-bottom: 12px;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled.button<{ engaged?: boolean }>`
  flex: 1;
  background: ${({ $engaged }) => ($engaged ? '#f39c12' : 'transparent')};
  color: ${({ $engaged }) => ($engaged ? '#fff' : 'inherit')};
  border: 1px solid var(--color-border, #d9d9d9);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
`;

const HangupButton = styled.button`
  flex: 1;
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
`;

const Keypad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-top: 8px;
`;

const KeypadButton = styled.button`
  background: transparent;
  border: 1px solid var(--color-border, #d9d9d9);
  border-radius: 6px;
  padding: 6px 0;
  cursor: pointer;
`;

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export interface ActiveCallPanelProps {
  call: ActiveCall;
  onToggleMute: () => void;
  onSendDigits: (digits: string) => void;
  onHangup: () => void;
}

export const ActiveCallPanel = ({ call, onToggleMute, onSendDigits, onHangup }: ActiveCallPanelProps) => {
  const [duration, setDuration] = useState(0);
  const [showKeypad, setShowKeypad] = useState(false);

  useEffect(() => {
    const interval = setInterval(
      () => setDuration(Math.max(0, Math.floor((Date.now() - call.connectedAt) / 1000))),
      1000,
    );
    return () => clearInterval(interval);
  }, [call.connectedAt]);

  return (
    <Panel role="region" aria-label="Active call">
      <ContactName>{call.contactName || call.contactPhone}</ContactName>
      <Detail>
        {call.contactName ? `${call.contactPhone} · ` : ''}
        {formatDuration(duration)}
      </Detail>
      <Controls>
        <ControlButton $engaged={call.muted} onClick={onToggleMute}>
          {call.muted ? 'Unmute' : 'Mute'}
        </ControlButton>
        <ControlButton $engaged={showKeypad} onClick={() => setShowKeypad((v) => !v)}>
          Keypad
        </ControlButton>
        <HangupButton onClick={onHangup}>
          Hang Up
        </HangupButton>
      </Controls>
      {showKeypad && (
        <Keypad>
          {DIGITS.map((digit) => (
            <KeypadButton key={digit} onClick={() => onSendDigits(digit)}>
              {digit}
            </KeypadButton>
          ))}
        </Keypad>
      )}
    </Panel>
  );
};
